import React, { useState, useEffect, useRef } from 'react';
import web3Service from '../services/Web3Service';
import './WalletSelectorComponent.css'; // You'll need to create this

const WalletSelectorComponent = ({ onConnect }) => {
  const [providers, setProviders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  useEffect(() => {
    // Detect available wallet providers on component mount
    const availableProviders = web3Service.getAvailableWalletProviders();
    setProviders(availableProviders);
    
    // Add click outside handler
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    
    // Add the event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleConnectWallet = async (providerName) => {
    try {
      // If MetaMask is requested but not found, offer to install it
      if (providerName === 'MetaMask' && !providers.find(p => p.name === 'MetaMask')) {
        window.open('https://metamask.io/download.html', '_blank');
        return;
      }
      
      // Special handling for MetaMask to ensure we're connecting to the right provider
      if (providerName === 'MetaMask') {
        // Specifically target MetaMask in providers array if available
        if (window.ethereum?.providers) {
          const metaMaskProvider = window.ethereum.providers.find(p => p.isMetaMask && !p.isPhantom);
          if (metaMaskProvider) {
            console.log('Using MetaMask provider from providers array');
            try {
              // Request accounts directly from the MetaMask provider
              await metaMaskProvider.request({ method: 'eth_requestAccounts' });
              
              // Manually initialize with this provider
              await web3Service.changeWalletProvider('MetaMask');
              
              setIsOpen(false);
              if (onConnect) onConnect();
              return;
            } catch (metaMaskError) {
              console.error('Error connecting to MetaMask provider:', metaMaskError);
              throw metaMaskError;
            }
          }
        }
      }
      
      // For other wallets or if the specific targeting failed, use the regular method
      await web3Service.changeWalletProvider(providerName);
      
      // Close the dropdown
      setIsOpen(false);
      
      // Call the parent callback
      if (onConnect) {
        onConnect();
      }
    } catch (error) {
      console.error(`Error connecting to ${providerName}:`, error);
      alert(`Error connecting to ${providerName}: ${error.message}`);
    }
  };
  
  // If no providers are detected, show install MetaMask button
  if (providers.length === 0) {
    return (
      <button 
        className="connect-button" 
        onClick={() => window.open('https://metamask.io/download.html', '_blank')}
      >
        Install MetaMask
      </button>
    );
  }
  
  // If MetaMask is the only provider, show a simple connect button
  if (providers.length === 1 && providers[0].name === 'MetaMask') {
    return (
      <button 
        className="connect-button" 
        onClick={() => handleConnectWallet('MetaMask')}
      >
        Connect MetaMask
      </button>
    );
  }
  
  // If multiple providers are available, show a dropdown
  return (
    <div className="wallet-selector" ref={dropdownRef}>
      <button 
        className="connect-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        Connect Wallet
      </button>
      
      {isOpen && (
        <div className="wallet-dropdown">
          {providers.map((provider) => (
            <div 
              key={provider.name}
              className="wallet-option"
              onClick={() => handleConnectWallet(provider.name)}
            >
              <span className="wallet-name">{provider.name}</span>
            </div>
          ))}
          
          {/* Always offer MetaMask as an option */}
          {!providers.find(p => p.name === 'MetaMask') && (
            <div 
              className="wallet-option install-option"
              onClick={() => window.open('https://metamask.io/download.html', '_blank')}
            >
              <span className="wallet-name">Install MetaMask</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WalletSelectorComponent;